import React from 'react';
import { Image } from '../Image/Image';
import './StoreBadgeLink.scss';

/**
 * StoreBadgeLink — official app store download badge rendered as a link.
 *
 * A store badge is fully determined by which store it points at: Apple and Google
 * each publish one official badge asset, one required label, and one listing URL for
 * the Amazon Seller app. Authors therefore select a store rather than supplying any
 * of these values, and every field below is a code-level override that no authoring
 * surface exposes.
 *
 * Both vendors require their badge artwork be reproduced unmodified, so the badge is
 * sized by height alone and keeps its intrinsic aspect ratio. The two assets are
 * authored at the same height, which is why a row of them aligns without cropping.
 */

const STORE_BADGES = {
  'app-store': {
    imageUrl: 'https://m.media-amazon.com/images/G/01/sell/images/apple-app-us.svg',
    alt: 'Download on the App Store',
    href: 'https://apps.apple.com/us/app/amazon-seller/id794141485',
  },
  'google-play': {
    imageUrl: 'https://m.media-amazon.com/images/G/01/sell/images/google-app-us.svg',
    alt: 'Get it on Google Play',
    href: 'https://play.google.com/store/apps/details?id=com.amazon.sellermobile.android',
  },
} as const;

export type StoreBadgeName = keyof typeof STORE_BADGES;

/** Both stores, in the order they are conventionally presented. */
export const STORE_BADGE_NAMES = Object.keys(STORE_BADGES) as StoreBadgeName[];

/**
 * CTA type values that render store badges. Blocks with a `ctaType` discriminator add
 * these to their own union so authors pick a badge from the CTA Type dropdown.
 */
export const STORE_BADGE_CTA_TYPES = ['store-badges', 'app-store', 'google-play'] as const;

export type StoreBadgeCtaType = (typeof STORE_BADGE_CTA_TYPES)[number];

/**
 * Narrows a block's `ctaType` to a badge type. Badges carry their own text and
 * destination, so callers use this to skip the authored-text guard other CTAs need.
 */
export const isStoreBadgeCtaType = (value: unknown): value is StoreBadgeCtaType =>
  STORE_BADGE_CTA_TYPES.includes(value as StoreBadgeCtaType);

export interface StoreBadgeLinkProps {
  /**
   * Which store's badge to render. Programmatic only — a consuming block selects
   * the store from its own CTA type, so this is never an authored field.
   * @polyIgnore
   */
  store: StoreBadgeName;

  /**
   * Overrides the store's listing URL. Needed where a store does not operate.
   * Programmatic only.
   * @polyIgnore
   */
  href?: string;

  /**
   * Overrides the badge artwork, for locales publishing their own official badge.
   * Programmatic only.
   * @polyIgnore
   */
  imageUrl?: string;

  /**
   * Overrides the badge label. Must remain the vendor's wording for that locale.
   * Programmatic only.
   * @polyIgnore
   */
  alt?: string;

  /**
   * ID attribute for the link element.
   * @polyIgnore
   */
  id?: string;

  /**
   * data-ld-append attribute for analytics tracking.
   * @polyIgnore
   */
  ldAppendix?: string;

  /**
   * Optional additional className.
   * @polyIgnore
   */
  className?: string;
}

export const StoreBadgeLink: React.FC<StoreBadgeLinkProps> = ({
  store,
  href,
  imageUrl,
  alt,
  id,
  ldAppendix,
  className,
}) => {
  const badge = STORE_BADGES[store];
  const classes = ['poly-store-badge', `poly-store-badge--${store}`, className].filter(Boolean).join(' ');

  return (
    <a
      href={href ?? badge.href}
      className={classes}
      id={id}
      data-ld-append={ldAppendix || undefined}
    >
      <Image bare url={imageUrl ?? badge.imageUrl} alt={alt ?? badge.alt} />
    </a>
  );
};

export interface StoreBadgeRowProps {
  /**
   * Stores to render, left to right. Programmatic only.
   * @polyIgnore
   */
  stores: readonly StoreBadgeName[];

  /**
   * ID prefix for the link elements; each badge is suffixed with its store name.
   * @polyIgnore
   */
  id?: string;

  /**
   * data-ld-append attribute applied to every badge in the row.
   * @polyIgnore
   */
  ldAppendix?: string;

  /**
   * Optional additional className.
   * @polyIgnore
   */
  className?: string;
}

export const StoreBadgeRow: React.FC<StoreBadgeRowProps> = ({ stores, id, ldAppendix, className }) => {
  if (stores.length === 0) return null;

  const classes = ['poly-store-badge-row', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {stores.map((store) => (
        <StoreBadgeLink
          key={store}
          store={store}
          id={id ? `${id}-${store}` : undefined}
          ldAppendix={ldAppendix}
        />
      ))}
    </div>
  );
};

export interface StoreBadgeCtaProps {
  /**
   * Badge CTA type, narrowed from a block's own `ctaType`. Programmatic only.
   * @polyIgnore
   */
  ctaType: StoreBadgeCtaType;

  /**
   * ID attribute, or ID prefix when the type renders both badges.
   * @polyIgnore
   */
  id?: string;

  /**
   * data-ld-append attribute for analytics tracking.
   * @polyIgnore
   */
  ldAppendix?: string;
}

/**
 * StoreBadgeCta — maps a badge `ctaType` value to the badges it renders.
 *
 * Every block with a `ctaType` discriminator routes badge types through here, so the
 * value-to-badges mapping lives in one place rather than being repeated per block.
 */
export const StoreBadgeCta: React.FC<StoreBadgeCtaProps> = ({ ctaType, id, ldAppendix }) =>
  ctaType === 'store-badges' ? (
    <StoreBadgeRow stores={STORE_BADGE_NAMES} id={id} ldAppendix={ldAppendix} />
  ) : (
    <StoreBadgeLink store={ctaType} id={id} ldAppendix={ldAppendix} />
  );
